import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { invalidateApiCache } from '../services/api';

const AuthContext = createContext(null);

// Intervalo do background sync do perfil (ms). 60s é agressivo o bastante para
// refletir mudanças de papel/verificação de email quase em tempo real, sem
// gerar carga significativa no backend.
const PROFILE_SYNC_INTERVAL_MS = 60_000;

// Campos "significativos" do usuário — mudanças aqui devem propagar para a UI.
// Outros campos (last_login etc.) são ignorados para evitar re-renders inúteis.
const SIGNIFICANT_FIELDS = [
  'role', 'nome', 'email', 'email_verificado', 'telefone', 'cpf',
  'municipio_id', 'foto', 'avatar_url', 'picture', 'blocked',
  'admin_permissions', 'permissions', 'onboarding_completo'
];

const pickSignificant = (u) => {
  if (!u || typeof u !== 'object') return null;
  const out = {};
  for (const k of SIGNIFICANT_FIELDS) out[k] = u[k];
  return JSON.stringify(out);
};

export const AuthProvider = ({ children }) => {
  // Inicializar estado diretamente do localStorage para evitar flash.
  // Sempre lê APENAS de `user`/`token` (canônicos). NÃO faz fallback para
  // chaves admin para evitar sessões "fantasma" quando alguém faz logout
  // do admin mas mantém token de empreendedor (ou vice-versa).
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) return JSON.parse(storedUser);
      return null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  
  const [loading, setLoading] = useState(false);

  // Sincronizar com localStorage quando houver mudanças em outras abas
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        setToken(e.newValue);
      }
      if (e.key === 'user') {
        try {
          setUser(e.newValue ? JSON.parse(e.newValue) : null);
        } catch {
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = useCallback((newToken, newUser, refreshToken = null) => {
    // Persistir nova sessão e limpar QUALQUER vestígio de sessão anterior
    // (admin OR empreendedor) para evitar identidade mista entre UI e API.
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    try { invalidateApiCache(); } catch { /* defensive */ }
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('admin_selected_municipio');
    // Drop in-memory API caches so the next signed-in user doesn't see
    // stale tenant-scoped data (categories/bairros/noticias/territories).
    try { invalidateApiCache(); } catch { /* defensive */ }
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return null;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      // Só atualiza o estado se houver mudança "significativa" no perfil,
      // evitando re-renders desnecessários da árvore React a cada polling.
      setUser((prev) => {
        const prevSig = pickSignificant(prev);
        const nextSig = pickSignificant(data);
        if (prevSig === nextSig) return prev;
        try { localStorage.setItem('user', JSON.stringify(data)); } catch { /* quota */ }
        return data;
      });
      return data;
    } catch (err) {
      return null;
    }
  }, [token]);

  // Ref para chamar o refresh dentro de listeners sem recriar o effect
  const refreshUserRef = useRef(refreshUser);
  useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);

  // Refetch user on mount when there's a token — corrige "banner de e-mail
  // não verificado" que persistia após o usuário confirmar (o snapshot em
  // localStorage era antigo). O `/auth/me` retorna o estado atual do backend
  // e atualiza state + localStorage. Roda apenas uma vez no boot.
  useEffect(() => {
    if (!token) return;
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== Background sync ====================
  // Poll silencioso do perfil a cada PROFILE_SYNC_INTERVAL_MS para propagar
  // mudanças feitas pelo admin (promoção de papel, verificação de e-mail,
  // bloqueio etc.) sem exigir logout/login. Também refaz ao voltar o foco
  // para a aba. Pausa quando a aba está oculta para poupar bateria/rede.
  useEffect(() => {
    if (!token) return undefined;

    let timerId = null;
    const start = () => {
      if (timerId) return;
      timerId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          refreshUserRef.current?.();
        }
      }, PROFILE_SYNC_INTERVAL_MS);
    };
    const stop = () => {
      if (timerId) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Ao voltar do background, sincroniza imediatamente e retoma o timer.
        refreshUserRef.current?.();
        start();
      } else {
        stop();
      }
    };
    const onFocus = () => {
      refreshUserRef.current?.();
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [token]);

  const isAuthenticated = useMemo(() => !!token && !!user, [token, user]);

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    refreshUser,
    loading,
    isAuthenticated,
  }), [user, token, login, logout, refreshUser, loading, isAuthenticated]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
